import {
    Box,
    Button,
    Popover,
    Typography,
    Divider,
    TextField,
    InputAdornment,
} from "@mui/material";
import { DateRange, AccessTime } from "@mui/icons-material";
import { useState } from "react";
import { CalendarGrid } from "./CalendarGrid";

const COLORS = {
    primary: "#0359D6",
    dark: "#090E34",
    stroke: "#E0E0E0",
    white: "#FFFFFF",
    body: "#637381",
};

/**
 * DateTimePickerField
 *
 * Single field that opens a popover with a calendar + time input.
 *
 * Props:
 *   value: { date: "DD/MM/YYYY", time: "HH:MM" | null } | null
 *   onChange(value): called with { date, time } or null when cleared
 *   defaultTime: "HH:MM" — hora pré-preenchida ao abrir sem valor (ex: "00:00" ou "23:59")
 */
export function DateTimePickerField({
    value,
    onChange,
    placeholder = "Selecione data e hora",
    defaultTime = "00:00",
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [initialDate, setInitialDate] = useState(null);
    const [pendingDate, setPendingDate] = useState(null); // "DD/MM/YYYY" or null
    const [pendingTime, setPendingTime] = useState(""); // "HH:MM" or ""

    const handleOpen = (e) => {
        if (value?.date) {
            setPendingDate(value.date);
            setPendingTime(value.time ?? defaultTime);
            const [day, month, year] = value.date.split("/");
            setInitialDate(new Date(year, month - 1, day));
        } else {
            setPendingDate(null);
            setPendingTime(defaultTime);
            setInitialDate(new Date());
        }
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleConfirm = () => {
        if (!pendingDate) {
            handleClose();
            return;
        }
        onChange({ date: pendingDate, time: pendingTime || null });
        handleClose();
    };

    const handleClear = () => {
        onChange(null);
        handleClose();
    };

    // Display text in the input
    const displayValue = value?.date
        ? value.time
            ? `${value.date} ${value.time}`
            : value.date
        : "";

    const open = Boolean(anchorEl);
    const id = open ? "datetime-picker-popover" : undefined;

    return (
        <Box>
            {/* Input trigger */}
            <Box sx={{ position: "relative" }}>
                <input
                    aria-describedby={id}
                    readOnly
                    value={displayValue}
                    onClick={handleOpen}
                    placeholder={placeholder}
                    style={{
                        height: "40px",
                        width: "100%",
                        borderRadius: "4px",
                        border: `1px solid ${COLORS.stroke}`,
                        paddingLeft: "36px",
                        paddingRight: "16px",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                        backgroundColor: COLORS.white,
                        boxSizing: "border-box",
                    }}
                />
                {/* Calendar icon */}
                <Box
                    sx={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: COLORS.body,
                        display: "flex",
                        alignItems: "center",
                        pointerEvents: "none",
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 21 20" fill="none">
                        <path
                            d="M18 3.3125H16.3125V2.625C16.3125 2.25 16 1.90625 15.5937 1.90625C15.1875 1.90625 14.875 2.21875 14.875 2.625V3.28125H6.09375V2.625C6.09375 2.25 5.78125 1.90625 5.375 1.90625C4.96875 1.90625 4.65625 2.21875 4.65625 2.625V3.28125H3C1.9375 3.28125 1.03125 4.15625 1.03125 5.25V16.125C1.03125 17.1875 1.90625 18.0938 3 18.0938H18C19.0625 18.0938 19.9687 17.2187 19.9687 16.125V5.25C19.9687 4.1875 19.0625 3.3125 18 3.3125ZM3 4.71875H4.6875V5.34375C4.6875 5.71875 5 6.0625 5.40625 6.0625C5.8125 6.0625 6.125 5.75 6.125 5.34375V4.71875H14.9687V5.34375C14.9687 5.71875 15.2812 6.0625 15.6875 6.0625C16.0937 6.0625 16.4062 5.75 16.4062 5.34375V4.71875H18C18.3125 4.71875 18.5625 4.96875 18.5625 5.28125V7.34375H2.46875V5.28125C2.46875 4.9375 2.6875 4.71875 3 4.71875ZM18 16.6562H3C2.6875 16.6562 2.4375 16.4062 2.4375 16.0937V8.71875H18.5312V16.125C18.5625 16.4375 18.3125 16.6562 18 16.6562Z"
                            fill="currentColor"
                        />
                    </svg>
                </Box>
            </Box>

            {/* Popover */}
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        style: {
                            marginTop: "8px",
                            padding: "20px",
                            borderRadius: "12px",
                            boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
                            width: "340px",
                        },
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                        color: COLORS.body,
                    }}
                >
                    <DateRange fontSize="small" />
                    <Typography
                        variant="overline"
                        sx={{
                            lineHeight: 1,
                            fontWeight: 600,
                            letterSpacing: "0.5px",
                            fontSize: "12px",
                        }}
                    >
                        Filtrar por Período
                    </Typography>
                </Box>

                {/* Calendário compartilhado */}
                <CalendarGrid
                    selectedDate={pendingDate}
                    onSelectDay={setPendingDate}
                    initialDate={initialDate}
                />

                {/* Time input */}
                <Divider sx={{ my: 2 }} />
                <Box>
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: COLORS.body,
                            mb: 1,
                            fontWeight: 500,
                        }}
                    >
                        Hora
                    </Typography>
                    <TextField
                        type="time"
                        size="small"
                        fullWidth
                        value={pendingTime}
                        onChange={(e) => setPendingTime(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccessTime fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                },
                            },
                        }}
                    />
                </Box>

                {/* Actions */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 3,
                    }}
                >
                    <Button
                        variant="text"
                        onClick={handleClear}
                        sx={{
                            bgcolor: "rgb(238, 238, 238)",
                            textTransform: "none",
                            borderRadius: "32px",
                            color: "#253044",
                        }}
                    >
                        Limpar
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleConfirm}
                        disabled={!pendingDate}
                        sx={{
                            bgcolor: COLORS.primary,
                            "&:hover": { bgcolor: "#254adb" },
                            textTransform: "none",
                            borderRadius: "32px",
                        }}
                    >
                        Confirmar
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
}
